import { Dialog } from "@material-ui/core";
import {
  Box,
  Button,
  Field,
  FieldArray,
  Form,
  Grid,
  Notification,
  TextField,
  Typography,
} from "components";
import SingleFileUpload from "components/advance/SingleFileUpload";
import {
  compose,
  withAuthorize,
  withFormik,
  withHooks,
  withTranslation,
} from "enhancers";
import { useMemo } from "react";
import { gql, Yup } from "utils/helper";
import { HOSPITAL_IMPORT_TABLE_API } from "./HospitalImportTable";

const CreateHospitalImportModal = ({
  t,
  isOpen,
  handleClickCancel,
  isButtonDisabled,
}: any) => (
  <Dialog open={isOpen}>
    <Form>
      <Box width={600}>
        <Box display="flex" justifyContent="space-between" mb={6}>
          <Typography variant="Header/20">
            {t(".createHospitalImport")}
          </Typography>
        </Box>
        <Grid container spacing={6} mb={3}>
          <Grid item xs={12}>
            <Field
              fast
              component={TextField}
              name="name"
              type="text"
              label={t(".name")}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FieldArray
              label={t(".importData")}
              component={SingleFileUpload}
              name="file"
              noStartIcon
              hasEditPermission
              addButtonLabel={t(".upload")}
              showEditButton={false}
              removeSingleImage={true}
              required
              simple={true}
              accept={[".xlsx", ".csv", ".xls"]}
            />
          </Grid>
        </Grid>
        <Box
          style={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}
        >
          <Button variant="outlined" onClick={handleClickCancel}>
            {t(".cancelButtonLabel")}
          </Button>
          <Button type="submit" disabled={isButtonDisabled}>
            {t(".okButtonLabel")}
          </Button>
        </Box>
      </Box>
    </Form>
  </Dialog>
);

const CREATE_HOSPITAL_IMPORT_API = {
  CREATE_HOSPITAL_IMPORT: gql`
    mutation CREATE_HOSPITAL_IMPORT($name: String!, $file: [Upload!]!) {
      createHospitalImport(input: { name: $name, file: $file }) {
        id
      }
    }
  `,
};

const enhancer = compose(
  withAuthorize(),

  withFormik({
    displayName: "CreateHospitalImportModal",
    mapPropsToValues: () => ({
      name: "",
    }),
    validationSchema: Yup.object().shape({
      name: Yup.string().required(".required"),
      file: Yup.mixed().required(".required"),
    }),
  }),
  withTranslation({
    prefix: "pages.main.hospitalImport.createModal",
  }),
  withHooks((props: any, hooks: any) => {
    const { onClose, isOpen, resetForm, dirty, error, isValid, t } = props;
    const { useMutation, useCallback, useHandleSubmit } = hooks;

    const [createHospitalImport] = useMutation(
      CREATE_HOSPITAL_IMPORT_API.CREATE_HOSPITAL_IMPORT,
      {
        refetchQueries: [
          { query: HOSPITAL_IMPORT_TABLE_API.HOSPITAL_IMPORT_LIST },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          Notification.success(t(".success"));
          handleClickCancel();
        },
      }
    );

    const handleClickCancel = useCallback(() => {
      onClose();
      resetForm();
    }, [onClose, resetForm]);

    useHandleSubmit(async (values: any) => {
      await createHospitalImport({
        variables: { ...values },
      });
    });

    const isButtonDisabled = useMemo(() => {
      return !(dirty && isValid) || props.values.file?.length === 0;
    }, [dirty, isValid, props.values.file]);

    return {
      handleClickCancel,
      isOpen,
      isButtonDisabled,
    };
  })
);

export default enhancer(CreateHospitalImportModal);
